<script setup lang="tsx">
import { ref, provide, nextTick } from 'vue'
import type { ComponentPublicInstance, ComponentInternalInstance } from 'vue'
import { watchDebounced } from '@vueuse/core'
import type { SelectorExpose } from '@/types'
import { SELECTOR_MODAL } from '@/utils/constant'

const props = defineProps({
  modelValue: [Number, String, Array],
  title: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue', 'open', 'close'])
const visible = ref(false)
const keywords = ref('')
let child: ComponentInternalInstance

function handleOk() {
  visible.value = false
  emit('update:modelValue', child?.exposed?.getResult())
  emit('close')
}

async function open() {
  keywords.value = ''
  visible.value = true
  emit('open')
  await nextTick()
  child.exposed?.handleFilter?.(keywords.value)
  child?.exposed?.setValue?.(props.modelValue)
}

watchDebounced(
  keywords,
  () => {
    child?.exposed?.handleFilter?.(keywords.value)
  },
  { debounce: 10 }
)

provide(SELECTOR_MODAL, {
  mountChild: (c: ComponentInternalInstance) => {
    child = c
  },
})

defineExpose({
  open,
})
</script>

<template>
  <v-overlay no-click-animation class="selector-modal" persistent :scrim="false" v-model="visible">
    <div class="selector-container">
      <div class="header">
        <div style="display: flex; align-items: center">
          <v-icon icon="mdi-chevron-left" @click="visible = false" />
          <span>经销商档案筛选</span>
        </div>
      </div>
      <div class="selector-header">
        <div class="header-title">{{ title }}</div>
        <div class="search-input">
          <input v-model="keywords" type="text" placeholder="输入关键字搜索" />
        </div>
      </div>
      <div class="list-wrapper">
        <div class="abs-full">
          <slot />
        </div>
      </div>
      <div class="selector-footer justify-center align-center">
        <button class="submit-button" @click="handleOk">完成</button>
      </div>
    </div>
  </v-overlay>
</template>

<style lang="scss">
.selector-modal {
  color: #333;
  > .v-overlay__content {
    position: absolute;
    bottom: 0;
    top: 2rem;
    width: 100%;
  }

  .header {
    height: 2.987rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0.747rem;
    border-bottom: 1px solid #f3f4f4;
    font-size: 0.96rem;
    background-color: #fff;
    border-radius: 0.747rem 0.747rem 0 0;
  }

  .selector-container {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .list-wrapper {
    flex: 1;
    background-color: #f9f9f9;
    position: relative;

    .abs-full {
      overflow-y: auto;
    }
  }

  .selector-header {
    height: 5.067rem;
    background-color: #e5f4fb;
    padding: 0 0.8rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .header-title {
    font-size: 0.85rem;
    font-weight: bold;
  }

  .search-input {
    margin-top: 0.587rem;
    width: 18.4rem;
    height: 1.81rem;
    background: rgba(243, 243, 243, 0.5);
    border-radius: 2.67rem;
    border: 1px solid var(--primary-color);
    padding: 0 1rem;

    input {
      width: 100%;
      height: 100%;
      outline: none;
    }
  }

  .selector-footer {
    height: 3.307rem;
    display: flex;
    background-color: #fff;
  }

  .submit-button {
    width: 17.33rem;
    height: 2.35rem;
    background: var(--primary-color);
    border-radius: 0.27rem;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 500;
  }
}
</style>
